import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Button, Icon } from 'components';
import { featureEnabled } from 'utils';
import { ActionModalAppointmentType } from 'contexts/modal/types';
import { useAppointmentActions, useBreakpoint } from 'hooks';
import { PermissionsType } from 'api/AppExternalController';
import { BuildJsonType } from '@bb/domain/dist/typeHelpers';
import { EnhancedAppointment } from '@bb/domain/dist/types';
import { CoopCancelReasonType } from '../../../../../app/DashboardComponents/CoopCancelAppointmentComponent';
import { ShowingDetailsPageUserType } from '../../types';

export type AutoActions = 'autoconfirm' | 'cancel' | 'deny' | 'confirm';

type ActionsProps = {
  agent: ShowingDetailsPageUserType;
  appointment: BuildJsonType<EnhancedAppointment>;
  thumbnail?: string;
  updateAppointment: () => void;
  dashboardId?: string;
  isPassed: boolean;
  feedbackSubmitted: boolean;
  showFeedback: boolean;
  scrollToFeedback: () => void;
  autoAction: AutoActions;
  tourFunctions?: {
    rescheduleFromTour: () => void;
    cancelFromTour: (
      reason: CoopCancelReasonType,
      details: string,
      onConfirm?: () => void,
    ) => Promise<void>;
    acceptSuggestionFromTour: () => Promise<void>;
    denySuggestionFromTour: () => void;
    closeAppointmentModal: () => void;
  };
  appointmentPermissions?: PermissionsType;
};

const buttonWithX = (text: string, onClick: () => void) => (
  <Button
    key={text}
    icon={<Icon type="x" color="black" width={12} height={12} />}
    onClick={onClick}
    data-cy={`showing-${text}-button`}
  >
    {text}
  </Button>
);

const cancelButton = (onClick: () => void) => buttonWithX('Cancel', onClick);
const denyButton = (onClick: () => void) => buttonWithX('Deny', onClick);

const suggestButton = (appointmentId: string, dashboardId?: string) => {
  let redirectUrl: string | undefined;
  if (dashboardId) {
    redirectUrl = `/external/#/dash/${dashboardId}/appointments/${appointmentId}/reschedule_contact`;
  } else {
    redirectUrl = `/#/appointments/${appointmentId}/time_change_contact?redirectTo=calendar`;
  }
  return (
    <Button
      key="Suggest"
      href={redirectUrl}
      icon={
        <Icon type="antdClockCircle" color="black" width={12} height={12} />
      }
      data-cy="showing-suggest-button"
    >
      Suggest
    </Button>
  );
};

const rescheduleButton = (
  appointmentId: string,
  userType: ShowingDetailsPageUserType,
  dashboardId?: string,
  rescheduleFromTour?: () => void,
) => {
  let redirectUrl: string | undefined =
    `/#/appointments/${appointmentId}/time_change?redirectTo=calendar`;
  if (dashboardId) {
    redirectUrl = `/external/#/dash/${dashboardId}/appointments/${appointmentId}/reschedule`;
  } else if (rescheduleFromTour) {
    redirectUrl = undefined;
  }
  return (
    <Button
      key="Reschedule"
      href={redirectUrl}
      onClick={() => rescheduleFromTour && rescheduleFromTour()}
      icon={
        <Icon
          type="antdClockCircle"
          color="black"
          width={12}
          height={12}
          offset={[0, -3]}
        />
      }
      data-cy="showing-reschedule-button"
    >
      {userType === 'agent' ? 'Suggest' : 'Reschedule'}
    </Button>
  );
};
const acceptButton = (onClick: () => void, loading: boolean) => (
  <Button
    key="Accept"
    icon={
      <Icon
        type="antdCheckOutlined"
        color="black"
        width={10}
        height={10}
        offset={[0, -3]}
      />
    }
    loading={loading}
    onClick={onClick}
    data-cy="showing-accept-button"
  >
    Accept
  </Button>
);

const GiveFeedbackButton = (scrollToFeedback: () => void) => (
  <Button
    key="Feedback"
    icon={
      <Icon type="antdLikeOutlined" width={10} height={10} offset={[0, -2]} />
    }
    onClick={scrollToFeedback}
    data-cy="showing-accept-button"
  >
    Give Feedback
  </Button>
);

const Actions: React.FC<ActionsProps> = ({
  agent,
  appointment,
  thumbnail,
  updateAppointment,
  dashboardId,
  isPassed,
  feedbackSubmitted,
  showFeedback,
  scrollToFeedback,
  autoAction,
  tourFunctions,
  appointmentPermissions,
}) => {
  const {
    coopCancel,
    coopConfirm,
    coopDeny,
    loading,
    clientConfirm,
    clientCancel,
    clientDeny,
    laConfirm,
    laCancel,
    laDeny,
  } = useAppointmentActions({
    appointment: {
      ...(appointment as unknown as ActionModalAppointmentType),
      listing: { ...appointment.listing, thumbnail },
    },
    onConfirm: updateAppointment,
    cancelFromTour: tourFunctions?.cancelFromTour,
    dashboardId,
  });
  const { md } = useBreakpoint();

  const status =
    appointment.status === 'confirm' && isPassed
      ? 'complete'
      : appointment.status;

  useEffect(() => {
    switch (status) {
      case 'new':
      case 'attempted':
        if (agent === 'agent') {
          if (autoAction === 'confirm' && appointmentPermissions?.confirm) {
            laConfirm();
          } else if (autoAction === 'deny' && appointmentPermissions?.deny) {
            laDeny();
          }
        } else if (agent === 'client') {
          if (
            (autoAction === 'autoconfirm' || autoAction === 'confirm') &&
            appointmentPermissions?.confirm
          ) {
            clientConfirm();
          } else if (autoAction === 'deny' && appointmentPermissions?.deny) {
            clientDeny();
          }
        } else if (agent === 'coop' && autoAction === 'cancel') {
          coopCancel();
        }
        break;
      case 'time_change':
        if (agent === 'agent') {
          if (!appointment.listingContactTimeChange) {
            if (autoAction === 'confirm' && appointmentPermissions?.confirm) {
              laConfirm();
            } else if (autoAction === 'deny' && appointmentPermissions?.deny) {
              laDeny();
            }
          } else if (
            autoAction === 'cancel' &&
            appointmentPermissions?.cancel
          ) {
            laCancel();
          }
        } else if (agent === 'client') {
          if (
            (autoAction === 'autoconfirm' || autoAction === 'confirm') &&
            appointmentPermissions?.confirm
          ) {
            clientConfirm();
          } else if (autoAction === 'deny' && appointmentPermissions?.deny) {
            clientDeny();
          }
        } else if (agent === 'coop' && autoAction === 'cancel') {
          coopCancel();
        }
        break;
      case 'confirm':
        if (
          agent === 'agent' &&
          autoAction === 'cancel' &&
          appointmentPermissions?.cancel
        ) {
          laCancel();
        } else if (
          agent === 'client' &&
          autoAction === 'cancel' &&
          appointmentPermissions?.cancel
        ) {
          clientCancel();
        } else if (agent === 'coop' && autoAction === 'cancel') {
          coopCancel();
        }
        break;
      case 'suggested_time_change':
        if (agent === 'coop') {
          if (autoAction === 'autoconfirm') {
            coopConfirm();
          }
          if (autoAction === 'deny') {
            coopDeny();
          }
        } else if (agent === 'client') {
          if (autoAction === 'cancel' && appointmentPermissions?.cancel) {
            clientCancel();
          }
        }
        break;
      default:
        break;
    }
  }, [status, autoAction]);

  let actionButtons: JSX.Element[] = [];

  switch (status) {
    case 'new':
    case 'attempted':
      if (agent === 'agent') {
        if (appointmentPermissions?.confirm) {
          actionButtons.push(acceptButton(laConfirm, loading));
        }
        if (appointmentPermissions?.deny) {
          actionButtons.push(denyButton(laDeny));
        }
        if (appointmentPermissions?.time_change) {
          actionButtons.push(suggestButton(appointment.id));
        }
      } else if (agent === 'client') {
        if (appointmentPermissions?.confirm) {
          actionButtons.push(acceptButton(clientConfirm, loading));
        }
        if (appointmentPermissions?.deny) {
          actionButtons.push(denyButton(clientDeny));
        }
        if (appointmentPermissions?.time_change) {
          actionButtons.push(suggestButton(appointment.id, dashboardId));
        }
      } else {
        actionButtons = [
          cancelButton(() => {
            tourFunctions?.closeAppointmentModal();
            coopCancel();
          }),
          rescheduleButton(
            appointment.id,
            agent,
            dashboardId,
            tourFunctions?.rescheduleFromTour,
          ),
        ];
      }
      break;
    case 'time_change':
      if (agent === 'agent') {
        if (!appointment.listingContactTimeChange) {
          if (appointmentPermissions?.confirm) {
            actionButtons.push(acceptButton(laConfirm, loading));
          }
          if (appointmentPermissions?.deny) {
            actionButtons.push(denyButton(laDeny));
          }
        } else if (appointmentPermissions?.cancel) {
          actionButtons.push(cancelButton(laCancel));
        }

        if (appointmentPermissions?.time_change) {
          actionButtons.push(suggestButton(appointment.id));
        }
      } else if (agent === 'client') {
        if (appointmentPermissions?.confirm) {
          actionButtons.push(acceptButton(clientConfirm, loading));
        }
        if (appointmentPermissions?.deny) {
          actionButtons.push(denyButton(clientDeny));
        }
        if (appointmentPermissions?.time_change) {
          actionButtons.push(suggestButton(appointment.id, dashboardId));
        }
      } else {
        actionButtons = [
          cancelButton(() => {
            tourFunctions?.closeAppointmentModal();
            coopCancel();
          }),
          rescheduleButton(
            appointment.id,
            agent,
            dashboardId,
            tourFunctions?.rescheduleFromTour,
          ),
        ];
      }
      break;
    case 'suggested_time_change':
      if (agent === 'coop') {
        actionButtons = [
          acceptButton(
            tourFunctions
              ? tourFunctions.acceptSuggestionFromTour
              : coopConfirm,
            loading,
          ),
          denyButton(
            tourFunctions ? tourFunctions.denySuggestionFromTour : coopDeny,
          ),
        ];
      } else if (agent === 'client') {
        if (appointmentPermissions?.cancel) {
          actionButtons.push(cancelButton(clientCancel));
        }
        if (appointmentPermissions?.time_change) {
          actionButtons.push(suggestButton(appointment.id, dashboardId));
        }
      } else {
        actionButtons = [
          cancelButton(() => {
            tourFunctions?.closeAppointmentModal();
            coopCancel();
          }),
          rescheduleButton(
            appointment.id,
            agent,
            dashboardId,
            tourFunctions?.rescheduleFromTour,
          ),
        ];
      }
      break;
    case 'confirm':
      if (agent === 'agent') {
        if (appointmentPermissions?.cancel) {
          actionButtons.push(cancelButton(laCancel));
        }
        if (appointmentPermissions?.time_change) {
          actionButtons.push(suggestButton(appointment.id));
        }
      } else if (agent === 'client') {
        if (appointmentPermissions?.cancel) {
          actionButtons.push(cancelButton(clientCancel));
        }
        if (appointmentPermissions?.time_change) {
          actionButtons.push(suggestButton(appointment.id, dashboardId));
        }
      } else {
        actionButtons = [
          cancelButton(() => {
            tourFunctions?.closeAppointmentModal();
            coopCancel();
          }),
          rescheduleButton(
            appointment.id,
            agent,
            dashboardId,
            tourFunctions?.rescheduleFromTour,
          ),
        ];
      }
      break;
    case 'complete':
      if (!feedbackSubmitted && agent === 'coop' && showFeedback) {
        actionButtons = [GiveFeedbackButton(scrollToFeedback)];
      }
      break;
    case 'time_passed':
    case 'deny':
    case 'cancel':
      break;
    default:
      return null;
  }

  return (
    <ActionButtonContainer isMobile={!md}>
      {actionButtons}
    </ActionButtonContainer>
  );
};

const ActionButtonContainer = styled.div<{ isMobile?: boolean }>`
  display: flex;
  margin: 16px 0;
  gap: 12px;
  & a {
    ${({ isMobile }) => isMobile && 'width: 50%'}
  }
`;

export default Actions;
